import { useEffect, useMemo } from "react";
import {
  Box,
  Button,
  Chip,
  Container,
  Grid,
  Paper,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import BoltIcon from "@mui/icons-material/Bolt";
import ShieldIcon from "@mui/icons-material/Shield";
import InsightsIcon from "@mui/icons-material/Insights";
import PhoneIphoneIcon from "@mui/icons-material/PhoneIphone";
import IntegrationInstructionsIcon from "@mui/icons-material/IntegrationInstructions";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";

const toDataUrl = (svg) => `url("data:image/svg+xml,${encodeURIComponent(svg)}")`;

const Landing = () => {
  const theme = useTheme();
  const isMdUp = useMediaQuery(theme.breakpoints.up("md"));
  const isLight = theme.palette.mode === "light";

  const dotsPattern = useMemo(
    () =>
      toDataUrl(
        `<svg width="160" height="160" viewBox="0 0 160 160" xmlns="http://www.w3.org/2000/svg"><defs><pattern id="dots" x="0" y="0" width="20" height="20" patternUnits="userSpaceOnUse"><circle cx="2" cy="2" r="2" fill="%23a7bfff" opacity="0.35"/></pattern></defs><rect width="160" height="160" fill="url(%23dots)"/></svg>`
      ),
    []
  );

  const waveOverlay = useMemo(
    () =>
      toDataUrl(
        `<svg width="1440" height="420" viewBox="0 0 1440 420" xmlns="http://www.w3.org/2000/svg"><defs><linearGradient id="grad" x1="0%" y1="0%" x2="100%" y2="0%"><stop offset="0%" stop-color="%2352b4ff" stop-opacity="0.32"/><stop offset="100%" stop-color="%23a78bfa" stop-opacity="0.22"/></linearGradient></defs><path d="M0,180 C240,320 480,120 720,210 C960,300 1200,160 1440,240 L1440,420 L0,420 Z" fill="url(#grad)" /></svg>`
      ),
    []
  );

  const accentGrid = useMemo(
    () =>
      toDataUrl(
        `<svg width="420" height="420" viewBox="0 0 420 420" xmlns="http://www.w3.org/2000/svg"><defs><linearGradient id="grid" x1="0%" y1="0%" x2="0%" y2="100%"><stop offset="0%" stop-color="%23ffffff" stop-opacity="0.22"/><stop offset="100%" stop-color="%23a0c4ff" stop-opacity="0.08"/></linearGradient></defs><g opacity="0.5" stroke="url(#grid)" stroke-width="1"><path d="M0 40h420" /><path d="M0 80h420" /><path d="M0 120h420" /><path d="M0 160h420" /><path d="M0 200h420" /><path d="M0 240h420" /><path d="M0 280h420" /><path d="M0 320h420" /><path d="M0 360h420" /><path d="M0 400h420" /><path d="M40 0v420" /><path d="M80 0v420" /><path d="M120 0v420" /><path d="M160 0v420" /><path d="M200 0v420" /><path d="M240 0v420" /><path d="M280 0v420" /><path d="M320 0v420" /><path d="M360 0v420" /><path d="M400 0v420" /></g></svg>`
      ),
    []
  );
  const cityGrid = accentGrid;

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "auto";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, []);

  const features = [
    {
      title: "Control total de turnos",
      description: "Gestiona turnos, liquidaciones y flotas con paneles listos para usar.",
      icon: <InsightsIcon color="primary" />,
    },
    {
      title: "Automatiza tu backoffice",
      description: "Exporta CSV, recibe notificaciones y sincroniza a tus herramientas.",
      icon: <IntegrationInstructionsIcon color="secondary" />,
    },
    {
      title: "Seguro y en la nube",
      description: "Roles diferenciados, historial y acceso multi-dispositivo.",
      icon: <ShieldIcon color="success" />,
    },
  ];

  const steps = [
    { label: "1. Crea tu cuenta", detail: "Registra tu flota y asigna roles a cada usuario." },
    { label: "2. Sube tus datos", detail: "Importa turnos vía CSV o captura desde la app móvil." },
    { label: "3. Controla en vivo", detail: "Consulta KPIs, integra con tu ERP y recibe alertas." },
  ];

  const integrations = ["CSV seguro", "Capacitor", "API REST", "Alertas push"];

  const mobileComposite = `${process.env.PUBLIC_URL}/capturamobile3.png`;
  const pcScreenshot = `${process.env.PUBLIC_URL}/capturapc.png`;

  return (
    <Box
      sx={{
        minHeight: "100vh",
        backgroundColor: isLight ? "#f5f7fb" : "#0a1024",
        color: isLight ? "#0b1225" : "#e6ebff",
      }}
    >
        <Box
          sx={{
            position: "relative",
            overflow: "hidden",
            background: "radial-gradient(circle at 15% 20%, rgba(82, 180, 255, 0.35), transparent 32%), radial-gradient(circle at 85% 12%, rgba(79, 230, 201, 0.28), transparent 26%), linear-gradient(135deg, #0a1635 0%, #0c1f3f 45%, #071427 100%)",
            color: "#f6f8ff",
            pb: 0,
            pt: { xs: 2, md: 4 },
          }}
        >
          <Box
            aria-hidden
            sx={{
              position: "absolute",
              inset: 0,
              backgroundImage: waveOverlay,
              backgroundRepeat: "no-repeat",
              backgroundSize: "cover",
              opacity: 0.4,
              filter: "blur(0px)",
            }}
          />
        <Box
          sx={{
            position: "relative",
            zIndex: 1,
            px: { xs: 3, sm: 4, md: 0 },
            width: "100vw",
            maxWidth: "100vw",
            boxSizing: "border-box",
          }}
        >
          <Grid
            container
            spacing={{ xs: 4, md: 3 }}
            alignItems="stretch"
            sx={{ position: "relative" }}
          >
            <Grid
              item
              xs={12}
              md={3}
              lg={3}
              sx={{
                display: "flex",
                justifyContent: { xs: "flex-start", md: "flex-start" },
                alignItems: "flex-end",
                order: { xs: 2, md: 1 },
                pl: { xs: "0 !important", md: 2 },
              }}
            >
              <Box
                component="img"
                src={mobileComposite}
              alt="Demo app móvil"
              sx={{
                width: { xs: "60%", sm: "54%", md: "110%" },
                maxWidth: 360,
                minWidth: 220,
                filter: "drop-shadow(0 18px 46px rgba(0,0,0,0.55))",
                pointerEvents: "none",
                alignSelf: "flex-end",
                mt: { xs: -7, md: -11 },
              }}
            />
            </Grid>

            <Grid
              item
              xs={12}
              md={5}
              lg={4}
              sx={{
                order: { xs: 1, md: 2 },
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <Stack
                spacing={3}
                sx={{
                  maxWidth: 540,
                  width: "100%",
                  alignItems: { xs: "center", md: "flex-start" },
                  textAlign: { xs: "center", md: "left" },
                }}
              >
                <Chip
                  label="TaxiBilling · Operativa inteligente"
                  color="primary"
                  sx={{
                    width: "fit-content",
                    fontWeight: 700,
                    borderRadius: "999px",
                    backgroundColor: "rgba(255,255,255,0.1)",
                    color: "#f6f8ff",
                    px: 1,
                  }}
                />
                <Typography variant={isMdUp ? "h3" : "h4"} fontWeight={800} lineHeight={1.2}>
                  Centraliza tu flota, liquidaciones y turnos en un solo lugar.
                </Typography>
                <Typography variant="h6" sx={{ color: "rgba(246,248,255,0.82)" }}>
                  Monitorea ingresos, conductores y vehículos en tiempo real. Integraciones
                  listas para CSV, notificaciones push y la app móvil para capturar turnos al vuelo.
                </Typography>

                <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                  <Button
                    size="large"
                    variant="contained"
                    color="secondary"
                    component={RouterLink}
                    to="/login"
                    startIcon={<BoltIcon />}
                    sx={{
                      px: 3,
                      py: 1.25,
                      fontWeight: 800,
                      borderRadius: 2,
                      boxShadow: "0 10px 40px rgba(0,0,0,0.25)",
                    }}
                  >
                    Iniciar sesión
                  </Button>
                  <Button
                    size="large"
                    variant="outlined"
                    component={RouterLink}
                    to="/register"
                    sx={{
                      px: 3,
                      py: 1.25,
                      fontWeight: 800,
                      borderRadius: 2,
                      borderColor: "rgba(255,255,255,0.6)",
                      color: "#f6f8ff",
                      "&:hover": { borderColor: "#fff", backgroundColor: "rgba(255,255,255,0.08)" },
                    }}
                  >
                    Crear cuenta
                  </Button>
                </Stack>

                <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                  <Chip
                    icon={<PhoneIphoneIcon sx={{ color: "#16ffe0" }} />}
                    label="App móvil lista para flota"
                    sx={{
                      backgroundColor: "rgba(22, 255, 224, 0.12)",
                      color: "#bffcf4",
                      fontWeight: 700,
                    }}
                  />
                  <Chip
                    icon={<CheckCircleOutlineIcon sx={{ color: "#93c5ff" }} />}
                    label="Onboarding en minutos"
                    sx={{
                      backgroundColor: "rgba(147, 197, 255, 0.18)",
                      color: "#eaf1ff",
                      fontWeight: 700,
                    }}
                  />
                </Stack>
              </Stack>
            </Grid>

            <Grid
              item
              xs={12}
              md={4}
              lg={5}
            sx={{
              order: { xs: 3, md: 3 },
              display: "flex",
            }}
          >
              <Box
                component="img"
                src={pcScreenshot}
                alt="Dashboard web"
                sx={{
                  width: { xs: "100%", md: "110%" },
                  maxWidth: "none",
                  display: "block",
                  objectFit: "contain",
                  filter: "drop-shadow(0 18px 46px rgba(0,0,0,0.45))",
                  borderRadius: 8,
                  mt: { xs: 4, md: 6 },
                  ml: { xs: 0, md: -2 },
                  mr: { xs: 0, md: -2 },
                }}
              />
            </Grid>
          </Grid>
        </Box>
      </Box>

      <Container maxWidth="lg" sx={{ py: { xs: 8, md: 10 } }}>
        <Grid container spacing={3}>
          {features.map((feature) => (
            <Grid item xs={12} md={4} key={feature.title}>
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  borderRadius: 3,
                  backgroundColor: isLight ? "#ffffff" : "rgba(255,255,255,0.04)",
                  border: `1px solid ${isLight ? "rgba(15,23,42,0.08)" : "rgba(255,255,255,0.08)"}`,
                  display: "flex",
                  flexDirection: "column",
                  gap: 1.5,
                  height: "100%",
                }}
              >
                <Box
                  sx={{
                    width: 52,
                    height: 52,
                    borderRadius: 2,
                    display: "grid",
                    placeItems: "center",
                    background: isLight
                      ? "linear-gradient(135deg, #0ea5e9, #6366f1)"
                      : "linear-gradient(135deg, rgba(34,211,238,0.35), rgba(99,102,241,0.35))",
                    boxShadow: "0 10px 24px rgba(0,0,0,0.25)",
                    "& svg": {
                      color: feature.title === "Seguro y en la nube" ? "#c4d7ff" : "#ffffff",
                      fontSize: 28,
                    },
                  }}
                >
                  {feature.icon}
                </Box>
                <Typography variant="h6" fontWeight={800}>
                  {feature.title}
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  {feature.description}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>

        <Grid
          container
          spacing={{ xs: 4, md: 6 }}
          alignItems="center"
          sx={{ mt: { xs: 6, md: 10 } }}
        >
          <Grid item xs={12} md={6}>
            <Typography variant="overline" fontWeight={800} color="primary">
              Cómo funciona
            </Typography>
            <Typography variant="h4" fontWeight={800} mt={1} mb={2}>
              Onboarding veloz, todo conectado.
            </Typography>
            <Stack spacing={2}>
              {steps.map((step) => (
                <Paper
                  key={step.label}
                  elevation={0}
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    backgroundColor: isLight ? "#ffffff" : "rgba(255,255,255,0.04)",
                    border: `1px solid ${isLight ? "rgba(15,23,42,0.08)" : "rgba(255,255,255,0.08)"}`,
                  }}
                >
                  <Typography fontWeight={800}>{step.label}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {step.detail}
                  </Typography>
                </Paper>
              ))}
            </Stack>
          </Grid>

          <Grid item xs={12} md={6}>
            <Paper
              elevation={0}
              sx={{
                p: 3,
                borderRadius: 3,
                backgroundColor: isLight ? "#0f172a" : "#0b132d",
                color: "#e8eeff",
                border: "1px solid rgba(255,255,255,0.08)",
                backgroundImage: accentGrid,
              }}
            >
              <Stack spacing={2}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <IntegrationInstructionsIcon />
                  <Typography fontWeight={800}>Integraciones listas</Typography>
                </Stack>
                <Typography color="rgba(232,238,255,0.8)">
                  Conecta tus datos sin fricción: exporta turnos directamente con SmartTD y comparte
                  reportes sin pasos intermedios.
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {integrations.map((chip) => (
                    <Chip
                      key={chip}
                      label={chip}
                      sx={{
                        color: "#e8eeff",
                        backgroundColor: "rgba(255,255,255,0.08)",
                        borderColor: "rgba(255,255,255,0.15)",
                      }}
                      variant="outlined"
                    />
                  ))}
                </Stack>
              </Stack>
            </Paper>
          </Grid>
        </Grid>

        <Paper
          elevation={0}
          sx={{
            mt: { xs: 8, md: 10 },
            p: { xs: 3, md: 4 },
            borderRadius: 3,
            background: isLight
              ? "linear-gradient(135deg, #dbeafe, #ede9fe)"
              : "linear-gradient(135deg, rgba(82,180,255,0.12), rgba(167,139,250,0.14))",
            border: `1px solid ${isLight ? "rgba(15,23,42,0.08)" : "rgba(255,255,255,0.08)"}`,
          }}
        >
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={8}>
              <Typography variant="h5" fontWeight={800} mb={1}>
                ¿Listo para probarlo?
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Ingresa a tu cuenta o regístrate gratis para ver el panel en vivo y conectar tus
                integraciones. La app móvil sigue entrando directo al login; esta landing es para la web.
              </Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Stack direction={{ xs: "column", sm: "row", md: "column" }} spacing={1.5}>
                <Button
                  variant="contained"
                  color="primary"
                  component={RouterLink}
                  to="/login"
                  startIcon={<BoltIcon />}
                  sx={{ fontWeight: 800 }}
                >
                  Ir al login
                </Button>
                <Button
                  variant="outlined"
                  color="secondary"
                  component={RouterLink}
                  to="/register"
                  startIcon={<ShieldIcon />}
                  sx={{ fontWeight: 800 }}
                >
                  Crear cuenta
                </Button>
              </Stack>
            </Grid>
          </Grid>
        </Paper>
      </Container>
    </Box>
  );
};

export default Landing;
